import { z } from "zod";

import type {
  CNSConversationSuggestion,
  CNSConversationSuggestions,
  CNSSuggestionAction,
  CNSSuggestionActivation,
} from "@/models/suggestion";
import {
  MENTION_WIRE_TYPES,
  matchesMentionIdPrefix,
} from "@/lib/mentions/referenceTypes";

export const suggestionPresentationSchema = z.object({
  label: z.string().min(1).max(120),
  supportingText: z.string().max(240).nullish(),
  accessibilityLabel: z.string().min(1).max(180),
});

const populateComposerActionSchema = z
  .object({
    type: z.literal("populate_composer"),
    prompt: z.string().min(1).max(2_000),
    reference: z
      .object({
        type: z.enum(MENTION_WIRE_TYPES),
        id: z.string().min(1).max(160),
        label: z.string().min(1).max(500),
      })
      .strict()
      .refine(
        (reference) => matchesMentionIdPrefix(reference.type, reference.id),
        "Composer reference type does not match its public ID",
      )
      .optional(),
  })
  .strict();

const focusToolCallActionSchema = z
  .object({
    type: z.literal("focus_tool_call"),
    toolCallId: z.string().min(1).max(160),
  })
  .strict();

const connectSourceActionSchema = z
  .object({
    type: z.literal("connect_source"),
    opportunityKey: z.string().min(1).max(160),
    pluginKey: z.string().min(1).max(64).nullish(),
  })
  .strict();

const openTrackerCaptureActionSchema = z
  .object({
    type: z.literal("open_tracker_capture"),
    experimentPublicId: z.string().min(1).max(160),
  })
  .strict();

const openTrackerLockReviewActionSchema = z
  .object({
    type: z.literal("open_tracker_lock_review"),
    experimentPublicId: z.string().min(1).max(160),
  })
  .strict();

/** Closed client action; strict branches reject mixed or unknown transport fields. */
export const suggestionActionSchema = z.discriminatedUnion("type", [
  populateComposerActionSchema,
  focusToolCallActionSchema,
  connectSourceActionSchema,
  openTrackerCaptureActionSchema,
  openTrackerLockReviewActionSchema,
]) satisfies z.ZodType<CNSSuggestionAction>;

export const conversationSuggestionSchema = z.object({
  id: z.string().min(1),
  presentation: suggestionPresentationSchema,
  action: suggestionActionSchema,
  actionSchemaVersion: z.literal(1),
  rank: z.number().int().nonnegative(),
  expiresAt: z.iso.datetime().nullish(),
}) satisfies z.ZodType<CNSConversationSuggestion>;

const conversationSuggestionsEnvelopeSchema = z.object({
  id: z.string().min(1),
  actorId: z.string().min(1),
  workspaceId: z.string().min(1),
  projectId: z.string().min(1),
  conversationId: z.string().min(1),
  headMessageId: z.string().min(1),
  revision: z.number().int().positive(),
  generatedAt: z.string().min(1),
  items: z.array(z.unknown()).max(6),
});

/**
 * A replacement remains useful when a newer server contributes an action this client does not
 * yet understand. Keep the closed action branches strict, but drop unsupported items instead of
 * discarding the known items and the authoritative replacement revision with them.
 */
export const conversationSuggestionsSchema = conversationSuggestionsEnvelopeSchema
  .transform((envelope) => ({
    ...envelope,
    items: envelope.items.flatMap((item) => {
      const parsed = conversationSuggestionSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    }),
  })) satisfies z.ZodType<CNSConversationSuggestions>;

const activationMessageSchema = z
  .object({
    id: z.string().min(1),
    parentId: z.string().nullish(),
    role: z.enum(["USER", "ASSISTANT", "TOOL", "SYSTEM"]),
    status: z.string().nullish(),
    error: z.string().max(60).nullish(),
    rawMessage: z.object({}).passthrough(),
    createdAt: z.string().min(1),
  })
  .strict();

export const suggestionActivationSchema = z
  .object({
    activationId: z.string().min(1),
    suggestionId: z.string().min(1),
    outcome: z.enum(["tool_call_materialized", "focus_existing_tool_call"]),
    toolCallId: z.string().min(1).max(160),
    assistantMessage: activationMessageSchema,
  })
  .strict() satisfies z.ZodType<CNSSuggestionActivation>;

export type SuggestionAction = z.infer<typeof suggestionActionSchema>;
export type ConversationSuggestion = z.infer<typeof conversationSuggestionSchema>;
export type ConversationSuggestions = z.infer<typeof conversationSuggestionsSchema>;
export type SuggestionActivation = z.infer<typeof suggestionActivationSchema>;

export function parseConversationSuggestions(value: unknown): ConversationSuggestions | null {
  const parsed = conversationSuggestionsSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseSuggestionActivation(value: unknown): SuggestionActivation | null {
  const parsed = suggestionActivationSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function isSuggestionCurrent(
  suggestion: Pick<ConversationSuggestion, "expiresAt">,
  now = Date.now(),
): boolean {
  return !suggestion.expiresAt || Date.parse(suggestion.expiresAt) > now;
}

const MAX_BROWSER_TIMEOUT_MS = 2_147_483_647;

/** Delay until the next visible item expires, capped so long-lived timers can reschedule safely. */
export function millisecondsUntilNextSuggestionExpiry(
  suggestions: ReadonlyArray<Pick<ConversationSuggestion, "expiresAt">>,
  now = Date.now(),
): number | null {
  let nextExpiry = Number.POSITIVE_INFINITY;
  for (const suggestion of suggestions) {
    if (!suggestion.expiresAt) continue;
    const expiresAt = Date.parse(suggestion.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt > now) {
      nextExpiry = Math.min(nextExpiry, expiresAt);
    }
  }
  if (!Number.isFinite(nextExpiry)) return null;
  return Math.min(nextExpiry - now + 1, MAX_BROWSER_TIMEOUT_MS);
}
